import type {
    FilterStates,
    FilterStatesKey,
} from '@modules-rocco/filters/types/filter-states';
import { SEARCH_CATEGORIES } from '@modules-rocco/search/config/search-service.config';
import { URL_PARAM_MAP } from '@modules-rocco/filters/config/filter-url.config';
import {
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSearchableListFilterState,
    isSearchQueryFilterState,
    isSingleValueFilterState,
} from '@modules-rocco/filters/filter-state/filter-state';
import type { SingleValueFilterState } from '@modules-rocco/filters/filter-state/single-value-filter-state';
import type { MultipleValuesFilterState } from '@modules-rocco/filters/filter-state/multiple-values-filter-state';
import type { SearchableListFilterState } from '@modules-rocco/filters/filter-state/searchable-list-filter-state';
import type { RangeFilterState } from '@modules-rocco/filters/filter-state/range-filter-state';
import type { FilterState } from '@modules-rocco/filters/filter-state/filter-state';
import type { SearchQueryFilterState } from '@modules-rocco/filters/filter-state/search-query-filter-state';
import type { LocationFilterState } from '@modules-rocco/filters/filter-state/location-filter-state';
import { parseURL } from '@modules-rocco/url/api/parse-url';
import type { URLParamValues } from '@modules-rocco/url/types';
import { generateURL } from '@modules-rocco/url/api/generate-url';
import type { SearchSortOptions } from '@modules-rocco/search/types/enum/search-sort-options';
import type { FeatureFlags } from '@modules/root';
import { isProductSuppliersPage } from '~/modules/url';
import { RangeDivisors } from '@modules/search/config/range-divisor';

/**
 * Non-filter params that shouldn't trigger browse mode.
 * These are utility params (pagination, search, navigation) rather than actual filters/sort.
 */
const NON_FILTER_PARAMS = [
    'page',
    'pageSize',
    'search',
    'category',
    'browse',
    'updateUserPrefs',
];

/**
 * Check if URL params contain any active filters.
 * This excludes pagination, search query, category, and utility params.
 *
 * When filters are active, we need to ensure the page stays in browse view
 * so the browse layout (product grid, pill filters) remains visible.
 *
 * @param params The URL parameters to check
 * @returns true if any filter params are active
 */
const hasActiveFilterParams = (params: URLParamValues): boolean => {
    return Object.keys(params).some((key) => {
        if (NON_FILTER_PARAMS.includes(key)) return false;
        const value = params[key];
        if (value === undefined || value === null) return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
    });
};

/**
 * Check whether the provided filter state currently represents any active filters.
 * This reuses the existing filter-to-URL conversion so the answer matches the URL
 * behaviour used elsewhere in the Rocco search experience.
 *
 * @param filterStates The current Rocco filter state object.
 * @param ignoreFilterKeys Filter keys to exclude from the active-filter check.
 * @param isSuppliersPage Whether suppliers-page URL mappings should be used.
 * @param includeSort Whether the current sort option should count as an active filter.
 * @returns `true` when at least one non-ignored filter is active.
 */
export function hasActiveFilterStates(
    filterStates: FilterStates,
    ignoreFilterKeys: FilterStatesKey[] = [],
    isSuppliersPage = false,
    includeSort = true,
): boolean {
    const params = convertFilterStateToURLParams(
        filterStates,
        ignoreFilterKeys,
        undefined,
        isSuppliersPage,
    );

    if (!includeSort) {
        delete params[getMappingKeyName('sortBy', isSuppliersPage)];
    }

    return hasActiveFilterParams(params);
}

/**
 * Create a new URL based on the filters' state and original URL
 * @param filterStates the filter states
 * @param originalURL
 * @param ignoreFilterKeys we will ignore these keys when parsing the filter states
 * @param defaultSort
 * @param featureSet
 * @returns
 */
export const mapFilterStatesToURL = (
    filterStates: FilterStates,
    originalURL: string,
    ignoreFilterKeys: FilterStatesKey[] = [],
    defaultSort?: SearchSortOptions,
    featureSet: FeatureFlags = [],
): string => {
    const isCategoryPage = !!SEARCH_CATEGORIES.find(
        (cfg) => cfg.categoryLink && originalURL.startsWith(cfg.categoryLink),
    );

    // Check if we're on the suppliers page
    const isSuppliersPage = isProductSuppliersPage(originalURL);

    // we will not add the category param if it's category page
    // instead, we will create new path based on the category state
    const params = convertFilterStateToURLParams(
        filterStates,
        isCategoryPage ? [...ignoreFilterKeys, 'category'] : ignoreFilterKeys,
        defaultSort,
        isSuppliersPage,
    );

    if (filterStates.shouldUpdateUserPreferences) {
        params.updateUserPrefs = true;
    }

    const { basePath, params: originalParams } = parseURL(
        originalURL,
        featureSet,
    );

    // Preserve browse parameter from original URL OR add it if filters are active
    const urlObj = new URL(originalURL, 'http://dummy');
    const browseParam = urlObj.searchParams.get('browse');
    const hasFilters = hasActiveFilterParams(params);

    // Add browse=true if:
    // 1. It was already in the original URL (preserve it), OR
    // 2. User has active filters (they're browsing, not just viewing the landing page)
    if (browseParam || hasFilters) {
        params.browse = browseParam || 'true';
    }

    // keep original info for the non category page
    if (!isCategoryPage) {
        if (originalParams?.category) {
            params.category = originalParams.category.value;
        } else {
            if (params.forSale || params.downloads) {
                params.category = 'products';
            }
        }

        if (originalParams?.search) {
            params.search = String(originalParams.search.value);
        }
    }

    const categoryState = filterStates.states.category;

    // create new path based on the category state for the category page
    let newPath = isCategoryPage
        ? (categoryState?.selectedCategory?.url ?? basePath)
        : basePath;

    // For suppliers page, ensure the path includes /suppliers/
    if (isSuppliersPage && newPath && newPath.startsWith('/products/')) {
        // Insert /suppliers/ after /products/ only if it's not already there
        const pathParts = newPath.split('/');
        if (
            pathParts.length >= 3 &&
            pathParts[1] === 'products' &&
            pathParts[2] !== 'suppliers'
        ) {
            pathParts.splice(2, 0, 'suppliers');
            newPath = pathParts.join('/');
        }
    }

    return generateURL(newPath, params, true, featureSet);
};

export const convertFilterStateToURLParams = (
    filterStates: FilterStates,
    ignoreFilterKeys: FilterStatesKey[] = [],
    defaultSort?: SearchSortOptions,
    isSuppliersPage = false,
): URLParamValues => {
    const ignoreKeys = [...ignoreFilterKeys, 'search', 'category'];

    // general states (not facet filters)
    const stateParams = Object.keys(filterStates.states).reduce(
        (acc, filterKey) => {
            if (ignoreKeys.includes(filterKey)) {
                return acc;
            }

            const paramKey = filterKey as Exclude<FilterStatesKey, 'search'>;
            const urlParamKey = getMappingKeyName(filterKey, isSuppliersPage);
            const state = filterStates.states?.[paramKey];

            if (!state) {
                return acc;
            }

            if (Array.isArray(state)) {
                return { ...acc, [urlParamKey]: state } as URLParamValues;
            }

            return {
                ...acc,
                ...convertFilterByState(urlParamKey, state),
            };
        },
        {} as URLParamValues,
    );

    // facet filters
    const facetParams =
        filterStates.facet?.facetOptions?.reduce((facetAcc, singleState) => {
            const subFacetParams = convertFilterByState(
                'facetOptions',
                singleState.state,
                singleState.id,
            );
            if (
                Array.isArray(facetAcc.facetOptions) &&
                Array.isArray(subFacetParams.facetOptions)
            ) {
                // Merge options from multiple facets into one array.
                return {
                    ...facetAcc,
                    ...subFacetParams,
                    facetOptions: [
                        ...(facetAcc.facetOptions as string[]),
                        ...(subFacetParams.facetOptions as string[]),
                    ],
                };
            }
            return {
                ...facetAcc,
                ...subFacetParams,
            };
        }, {} as URLParamValues) ?? {};

    // sort
    const sortParamName = getMappingKeyName('sortBy', isSuppliersPage);
    const sortParams: URLParamValues =
        filterStates.sortBy && filterStates.sortBy !== defaultSort
            ? {
                  [sortParamName]: filterStates.sortBy,
              }
            : {};

    return { ...stateParams, ...facetParams, ...sortParams } as URLParamValues;
};

const convertFilterByState = (
    filterKey: string,
    state: FilterState,
    subFilterKey?: string,
): URLParamValues => {
    if (isSingleValueFilterState(state)) {
        return convertSingleValueFilterState(filterKey, state);
    }

    if (isSearchableListFilterState(state)) {
        return convertSearchableListFilterState(filterKey, state, subFilterKey);
    }

    if (isMultipleValuesFilterState(state)) {
        return convertMultipleValuesFilterState(filterKey, state);
    }

    if (isRangeFilterState(state)) {
        return convertRangeFilterState(filterKey, state);
    }

    if (isSearchQueryFilterState(state)) {
        return convertSearchQueryFilterState(filterKey, state);
    }

    if (isLocationFilterState(state)) {
        return convertLocationFilterState(state);
    }

    throw new Error(
        '[filter-url-helpers] convertFilterByState: unsupported filter state:' +
            JSON.stringify(state),
    );
};

const convertSingleValueFilterState = (
    filterKey: string,
    state: SingleValueFilterState,
): URLParamValues => {
    return state.checked ? { [filterKey]: 1 } : {};
};

const convertMultipleValuesFilterState = (
    filterKey: string,
    state: MultipleValuesFilterState,
): URLParamValues => {
    if (!state.selectedItems?.length) {
        return {};
    }

    const values = state.selectedItems
        .filter((item) => !!item.value)
        .map((item) => item.value!);

    return {
        [filterKey]: state.allowMultipleSelectedItems
            ? values
            : (values[0] ?? ''),
    };
};

const convertSearchableListFilterState = (
    filterKey: string,
    state: SearchableListFilterState,
    subFilterKey?: string,
): URLParamValues => {
    const params: URLParamValues = {};
    const subFilterPrefix = subFilterKey ? `${subFilterKey}-` : '';

    // temp disable
    // it never worked, and it doesn't have any impact on filtered products.
    // It just for saving state of filter search.
    // For example you have a suppliers list and you are filtering it using a query: "Architecture".
    // You are getting a list of suppliers which contains all suppliers matching the query + added URL query param `suppliers_search=Architecture`.
    // But prod doesn't use / restore this parameter into filter.

    // if (state.search.length > 0) {
    //     params[`${filterKey}_search`] = `${subFilterPrefix}${state.search}`;
    // }
    //
    // if (state?.items?.length > DEFAULT_SEARCH_LIST_SIZE) {
    //     params[`${filterKey}_size`] = state.items.length;
    // }

    const selectedItems = state?.selectedItems ?? [];

    if (selectedItems.length > 0) {
        const items = selectedItems.map(
            (item) => `${subFilterPrefix}${item.value}`,
        );

        params[filterKey] = state.allowMultipleSelectedItems
            ? items
            : (items[0] ?? '');
    }

    return params;
};

const convertRangeFilterState = (
    filterKey: string,
    state: RangeFilterState,
): URLParamValues => {
    if (!state.value) {
        return {};
    }

    const { min, max } = state.value;
    const { min: availableMin, max: availableMax } = state.range;

    if (min === availableMin && max === availableMax) {
        return {};
    }

    // Convert internal values (multiplied by rangeDivisor) to display values for URL
    // e.g., if rangeDivisor is 100, 25000 becomes 250 in the URL
    const rangeDivisor =
        state.rangeDivisor ??
        RangeDivisors[filterKey as keyof typeof RangeDivisors] ??
        1;
    const displayMin = min / rangeDivisor;
    const displayMax = max / rangeDivisor;

    return { [filterKey]: `${displayMin},${displayMax}` };
};

const convertSearchQueryFilterState = (
    filterKey: string,
    state: SearchQueryFilterState,
): URLParamValues => {
    if (!state.query) {
        return {};
    }

    return { [filterKey]: state.query };
};

const convertLocationFilterState = (
    state: LocationFilterState,
): URLParamValues => {
    if (!state.selectedLocation) {
        return {};
    }

    return Object.fromEntries(
        Object.entries(state.selectedLocation)
            .map(([paramKey, value]) => {
                return [getMappingKeyName(paramKey), value];
            })
            .filter((entry) => entry[1]),
    );
};

const getMappingKeyName = (key: string, isSuppliersPage = false): string => {
    // On suppliers page, use 'brands' instead of 'company' for professionals filter
    if (isSuppliersPage && key === 'professionals') {
        return 'brands';
    }
    return URL_PARAM_MAP[key] ?? key;
};
